<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Navbar />
    <div class="notifications-page">
      <el-card shadow="always" class="notifications-card">
        <template #header>
          <div class="notifications-header">
            <h2>Notifications</h2>
            <el-button v-if="unreadCount" size="small" @click="markAllAsRead">Tout marquer comme lu</el-button>
          </div>
        </template>
        <el-empty v-if="loaded && notifications.length === 0" description="Aucune notification pour le moment." />
        <ul v-else class="notification-list">
          <li
            v-for="notification in notifications"
            :key="notification.id"
            :class="['notification-item', { unread: !notification.isRead }]"
            @click="openNotification(notification)"
          >
            <el-avatar
              :size="36"
              :src="notification.actor ? getProfilePictureUrl(notification.actor.profile_picture) : ''"
            />
            <div class="notification-body">
              <p class="notification-text">
                <strong>{{ actorName(notification) }}</strong> {{ describe(notification) }}
              </p>
              <span class="notification-date">{{ formatDate(notification.createdAt) }}</span>
            </div>
            <span v-if="!notification.isRead" class="unread-dot" aria-label="Non lue" />
          </li>
        </ul>
      </el-card>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import api, { API_BASE_URL } from '@/api'
import { notifyError } from '@/notify'

// Texte affiché après le pseudo de l'auteur, selon le type de notification
const DESCRIPTIONS = {
  POST_COMMENT: 'a commenté votre publication',
  COMMENT_REPLY: 'a répondu à votre commentaire',
  FRIEND_REQUEST: "vous a envoyé une demande d'ami",
  FRIEND_ACCEPTED: "a accepté votre demande d'ami",
  PRIVATE_MESSAGE: 'vous a envoyé un message privé',
}

export default {
  components: {
    Navbar,
  },

  data() {
    return {
      notifications: [],
      loaded: false,
    }
  },

  computed: {
    unreadCount() {
      return this.notifications.filter((n) => !n.isRead).length
    },
  },

  async created() {
    await this.fetchNotifications()
  },

  mounted() {
    document.body.style.backgroundColor = 'rgba(21, 109, 123)'
  },

  methods: {
    async fetchNotifications() {
      try {
        const response = await api.get('/user/notifications')
        this.notifications = (response.data && response.data.notifications) || []
      } catch (error) {
        notifyError(error, 'Impossible de charger les notifications')
      } finally {
        this.loaded = true
      }
    },

    actorName(notification) {
      return notification.actor ? notification.actor.username : 'Un utilisateur supprimé'
    },

    describe(notification) {
      return DESCRIPTIONS[notification.type] || 'a interagi avec vous'
    },

    // Page ouverte au clic : la publication, la conversation, la liste d'amis ou le profil
    targetRoute(notification) {
      const actor = notification.actor
      switch (notification.type) {
        case 'POST_COMMENT':
        case 'COMMENT_REPLY':
          return notification.postId ? { name: 'Post', params: { postId: notification.postId } } : null
        case 'FRIEND_REQUEST':
          return '/Friends'
        case 'FRIEND_ACCEPTED':
          return actor ? { name: 'UserProfile', params: { username: actor.username } } : null
        case 'PRIVATE_MESSAGE':
          return actor ? { name: 'Conversation', params: { userId: actor.id } } : '/Messages'
        default:
          return null
      }
    },

    async openNotification(notification) {
      if (!notification.isRead) {
        try {
          await api.patch(`/user/notifications/${notification.id}/read`)
          notification.isRead = true
        } catch (error) {
          // La navigation reste possible même si le marquage échoue
        }
      }
      const target = this.targetRoute(notification)
      if (target) this.$router.push(target)
    },

    async markAllAsRead() {
      try {
        await api.patch('/user/notifications/read-all')
        this.notifications.forEach((n) => { n.isRead = true })
      } catch (error) {
        notifyError(error, 'Impossible de marquer les notifications comme lues')
      }
    },

    getProfilePictureUrl(path) {
      return `${API_BASE_URL}/${path}`
    },

    formatDate(dateString) {
      const options = { day: 'numeric', month: 'long', hour: 'numeric', minute: 'numeric' }
      return new Date(dateString).toLocaleDateString('fr-FR', options)
    },
  },
}
</script>

<style scoped>
.notifications-page {
  padding: 70px 20px 20px;
  max-width: 700px;
  margin: 0 auto;
}

.notifications-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.notifications-card h2 {
  margin: 0;
  color: #303133;
}

.notification-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.notification-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
}

.notification-item:hover {
  background: #f5f7fa;
}

.notification-item.unread {
  background: #ecf5ff;
}

.notification-body {
  flex: 1;
  min-width: 0;
}

.notification-text {
  margin: 0;
  color: #303133;
}

.notification-date {
  font-size: 12px;
  color: #909399;
}

.unread-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #156d7b;
  flex-shrink: 0;
}
</style>
